import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import AppIcon from '../components/AppIcon';
import { getErrorMessage } from '../api/errors';
import { authService } from '../services';
import {
  loadPendingVerifyEmail,
  savePendingVerifyEmail,
} from '../utils/emailVerification';

type VerifyEmailPendingState = {
  email?: string;
  message?: string;
};

export default function VerifyEmailPending() {
  const location = useLocation();
  const state = (location.state as VerifyEmailPendingState | null) ?? {};

  const [email, setEmail] = useState('');
  const message =
    state.message ??
    'Kayıt başarılı. Giriş yapabilmek için e-posta adresinizi doğrulamanız gerekiyor.';
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const fromState = state.email?.trim() ?? '';
    const fromStorage = loadPendingVerifyEmail();
    const resolved = fromState || fromStorage;
    if (resolved) {
      setEmail(resolved);
      savePendingVerifyEmail(resolved);
    }
  }, [state.email]);

  const handleResend = async () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setResendError('Doğrulama e-postası göndermek için e-posta adresinizi girin.');
      return;
    }

    setResendError('');
    setResendMessage('');
    setResending(true);

    try {
      const result = await authService.resendVerificationEmail({ email: normalized });
      savePendingVerifyEmail(normalized);
      setResendMessage(result.message);
    } catch (err) {
      setResendError(getErrorMessage(err, 'Doğrulama e-postası gönderilemedi.'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="container auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <span className="auth-icon">
            <AppIcon icon={MailCheck} size={40} />
          </span>
          <h1>E-postanızı Kontrol Edin</h1>
          <p>{message}</p>
        </div>

        <div className="auth-info">
          {email ? (
            <p>
              Doğrulama bağlantısı <strong>{email}</strong> adresine gönderildi.
            </p>
          ) : (
            <p>Size bir doğrulama e-postası gönderdik.</p>
          )}
          <p>Gelen kutunuzu ve spam klasörünüzü kontrol edin.</p>
          <p className="auth-hint">
            Bağlantının süresi dolduysa veya e-postayı silmişseniz aşağıdan yeni doğrulama maili
            isteyebilirsiniz.
          </p>
        </div>

        {resendError && <div className="auth-error">{resendError}</div>}
        {resendMessage && <div className="auth-success">{resendMessage}</div>}

        <div className="auth-actions">
          <div className="form-group">
            <label htmlFor="resendEmail">E-posta</label>
            <input
              id="resendEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@email.com"
              required
              autoComplete="email"
            />
          </div>

          <button
            type="button"
            className="btn btn-outline btn-lg btn-block"
            disabled={resending}
            onClick={() => void handleResend()}
          >
            {resending ? 'Gönderiliyor...' : 'Doğrulama E-postasını Tekrar Gönder'}
          </button>

          <Link to="/login" className="btn btn-primary btn-lg btn-block">
            Giriş Sayfasına Git
          </Link>
        </div>
      </div>
    </div>
  );
}
